import React, { useState } from 'react'
import { cx } from '@emotion/css'
import PropTypes from 'prop-types'

import CaretDownIcon from '@offerzen/zenicon/CaretDownIcon'
import FormFieldset from '../../../elements/FormFieldset'
import FormLabel from '../../../elements/FormLabel'
import FormHelpText from '../../../elements/FormHelpText'
import FormErrorMessage from '../../../elements/FormErrorMessage'
import AccessibleIcon from '../../../elements/AccessibleIcon'

import Flag from '../Flag'
import { phonePrefixMap } from './dialCodes'
import styles, { wrapperTheme } from './styles'

const phonePrefixList = Object.keys(phonePrefixMap)
const suggestedPrefixList = Object.values(phonePrefixMap)
  .filter(item => item.suggestedOption)
  .map(item => item.code)

const getPrefix = countryCode => (phonePrefixMap[countryCode] ? phonePrefixMap[countryCode].prefix : '')

const selectLabel = prefix => {
  const country = phonePrefixMap[prefix]
  return `${country.name} ${getPrefix(prefix)}`
}

function escapedPrefix(regexString) {
  return getPrefix(regexString).replace(/\+/g, '\\$&')
}

const combineIntoString = (prefix, number) => `${getPrefix(prefix)}${number}`

const separateFromString = string => {
  let foundPrefix = phonePrefixList.find(prefix => {
    const regex = new RegExp(String.raw`^${escapedPrefix(prefix)}`)
    return string.search(regex) > -1
  })

  foundPrefix = foundPrefix || ''

  return {
    prefix: foundPrefix,
    number: string.slice(getPrefix(foundPrefix).length),
  }
}

const CountryCodePhoneInput = ({
  id,
  label = 'Cellphone number',
  hideLabel = false,
  onChange,
  onBlur,
  value = {
    prefix: '',
    number: '',
  },
  errored = false,
  errorText = 'Error',
  helpText,
  theme = 'primary',
  className,
}) => {
  let valueObject = value
  if (typeof value === 'string') {
    valueObject = separateFromString(value)
  }

  const [prefixValue, setPrefixValue] = useState(valueObject.prefix)
  const [numberValue, setNumberValue] = useState(valueObject.number)

  const onSelectChange = ev => {
    setPrefixValue(ev.target.value)

    // strip leading 0 and non-digit characters
    const newNumberValue = numberValue.replace(/^0|[^\d]/g, '')
    setNumberValue(newNumberValue)

    if (onChange) onChange(combineIntoString(ev.target.value, newNumberValue))
  }
  const onInputChange = ev => {
    ev.preventDefault()
    // cannot type non-digit characters
    const newValue = ev.target.value.replace(/[^\d]/g, '')
    setNumberValue(newValue)
    if (onChange) onChange(combineIntoString(prefixValue, newValue))
  }

  const onInputBlur = ev => {
    // strip leading 0
    const newNumberValue = ev.target.value.replace(/^0/g, '')
    setNumberValue(newNumberValue)

    const newValue = combineIntoString(prefixValue, newNumberValue)
    if (onChange) onChange(newValue)
  }

  const emptyLabel = '- select a country code -'

  const onComponentBlur = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      // Don't trigger when swapping focus between children
      if (onBlur) onBlur() // Don't update the value at the component level (only at the input level)
    }
  }

  return (
    <FormFieldset legend={label} className={cx(styles.fieldset, className)} hideLegend={hideLabel}>
      <div className={cx(styles.wrapper, wrapperTheme(theme), errored && styles.errorWrapper)} onBlur={onComponentBlur}>
        <FormLabel label="Country telephone prefix" htmlFor={`country-code-${id}`} className={styles.selectLabel} />
        <span aria-hidden="true" className={styles.currentFlag}>
          {prefixValue ? <Flag countryCode={phonePrefixMap[prefixValue].code} /> : <>🌐</>}
        </span>
        <AccessibleIcon
          icon={CaretDownIcon}
          size={styles.caret.width}
          color={styles.caret.color}
          ariaHidden
          className={styles.caret.styles}
        />
        <span aria-hidden="true" className={styles.currentPrefix}>
          {prefixValue ? getPrefix(prefixValue) : '--'}
        </span>
        <select id={`country-code-${id}`} className={styles.select} value={prefixValue} onChange={onSelectChange}>
          <option value="" label={emptyLabel} />
          <optgroup label="Suggested country codes">
            {suggestedPrefixList.map(prefix => (
              <option key={prefix} value={prefix}>
                {selectLabel(prefix)}
              </option>
            ))}
          </optgroup>
          <optgroup label="All country codes">
            {phonePrefixList.map(prefix => (
              <option key={prefix} value={prefix}>
                {selectLabel(prefix)}
              </option>
            ))}
          </optgroup>
        </select>

        <FormLabel label="Telephone number" htmlFor={id} className={styles.inputLabel} />
        <input type="tel" className={styles.input} id={id} value={numberValue || ''} onChange={onInputChange} onBlur={onInputBlur} />
      </div>
      {Boolean(helpText && !errored) && <FormHelpText text={helpText} />}
      {Boolean(errored) && <FormErrorMessage text={errorText} />}
    </FormFieldset>
  )
}

CountryCodePhoneInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  hideLabel: PropTypes.bool,
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'dark', 'tertiary', 'info']),
  className: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      prefix: PropTypes.string,
      number: PropTypes.number,
    }),
  ]),
  errored: PropTypes.bool,
  errorText: PropTypes.string,
  helpText: PropTypes.string,
}

export default CountryCodePhoneInput
